/**
 * The calendar the app's two date controls draw — DatePicker (one day, in the cabinet's forms) and
 * DateRangeFilter (a period, in the public filters): the names of its months and weekdays, the six
 * weeks a month is shown as, and the ISO day ("2026-09-01") both carry as their value.
 *
 * One copy. The two components each wrote all of this out for themselves, the same way, and two
 * calendars that must agree on which day a grid starts on only agreed because both copies did.
 *
 * ISO days are read and written as strings, never through Date's parser: Date reads a bare
 * "2026-09-01" as UTC midnight, which is the day before anywhere west of Greenwich.
 */
import type { CalendarCell } from '@/features/common/types/CalendarCell';
import type { IsoDayParts } from '@/features/common/types/IsoDayParts';

export const MONTH_NAMES = {
  ro: [
    'Ianuarie',
    'Februarie',
    'Martie',
    'Aprilie',
    'Mai',
    'Iunie',
    'Iulie',
    'August',
    'Septembrie',
    'Octombrie',
    'Noiembrie',
    'Decembrie',
  ],
  en: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
};

/** Monday first, as MUD's calendar and a Romanian calendar both start the week. */
export const WEEKDAY_NAMES = {
  ro: ['L', 'Ma', 'Mi', 'J', 'V', 'S', 'D'],
  en: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
};

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** The ISO day for a year, a month counted from 0 (as Date counts them) and a day. */
export function isoDay(year: number, month: number, day: number): string {
  return `${year}-${pad2(month + 1)}-${pad2(day)}`;
}

/**
 * A day and a time of day typed in local time, as the instant the API stores. A time left empty is
 * midnight: a date alone still names a day, and an empty time must not make it an invalid date.
 */
export function combineDateAndTime(date: string, time: string): string {
  return new Date(`${date}T${time || '00:00'}`).toISOString();
}

/** Today, as an ISO day in local time. */
export function todayIso(): string {
  const d = new Date();
  return isoDay(d.getFullYear(), d.getMonth(), d.getDate());
}

/** An ISO day's parts, the month counted from 0 — or null when the value is not an ISO day. */
export function parseIsoDay(value: string | null | undefined): IsoDayParts | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value ?? '');
  return m ? { year: Number(m[1]), month: Number(m[2]) - 1, day: Number(m[3]) } : null;
}

/** "2026-09-01" as MUD writes a date, "01/09/2026"; anything that is not an ISO day, unchanged. */
export function formatIsoDay(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
}

/**
 * A date typed into a field, as an ISO day — or null when it is not a real date.
 *
 * DD/MM/YYYY is what MUD's placeholder asks for; one-digit days and months, and dots or dashes for
 * slashes, are what people type anyway, so they are read too. "31/02/2026" is refused rather than
 * rolled over into March, which is what Date would do with it.
 */
export function parseTypedDate(text: string): string | null {
  const m = /^\s*(\d{1,2})[./-](\d{1,2})[./-](\d{4})\s*$/.exec(text);
  if (!m) return null;
  const day = Number(m[1]);
  const month = Number(m[2]);
  const year = Number(m[3]);
  if (month < 1 || month > 12 || day < 1) return null;
  // Day 0 of the next month is the last day of this one.
  if (day > new Date(year, month, 0).getDate()) return null;
  return isoDay(year, month - 1, day);
}

/**
 * The six weeks the calendar shows for a month (`month` counted from 0), Monday first, including
 * the days of the months either side that fill the first and last rows — MUD's `is-outside` days,
 * which DatePicker greys out and cannot pick.
 */
export function monthGrid(year: number, month: number): CalendarCell[] {
  const first = new Date(year, month, 1);
  const lead = (first.getDay() + 6) % 7; // days before the 1st, Monday = 0
  return Array.from({ length: 42 }, (_, i) => {
    const d = new Date(year, month, 1 - lead + i);
    return {
      iso: isoDay(d.getFullYear(), d.getMonth(), d.getDate()),
      day: d.getDate(),
      outside: d.getMonth() !== month,
    };
  });
}
